<template>
  <header class="app-header">
    <div class="main-header">
      <div class="app-header-left">
        <EngineLink
          url="/"
          :display-contents="false"
          idle-class="app-header-brand-link"
          active-class="app-header-brand-link"
        >
          <h1 class="app-header-brand">
            Q3-PO
          </h1>
        </EngineLink>
        <nav class="app-header-nav" aria-label="Primary">
          <EngineLink
            url="/projects"
            :display-contents="false"
            :idle-class="navLinkClass('/projects')"
            :active-class="navLinkClass('/projects')"
          >
            projects
          </EngineLink>
          <EngineLink
            url="/board"
            :display-contents="false"
            :idle-class="navLinkClass('/board')"
            :active-class="navLinkClass('/board')"
          >
            board
          </EngineLink>
          <EngineLink
            url="/workspace"
            :display-contents="false"
            :idle-class="navLinkClass('/workspace')"
            :active-class="navLinkClass('/workspace')"
          >
            workspace
          </EngineLink>
          <EngineLink
            url="/tasks"
            :display-contents="false"
            :idle-class="navLinkClass('/tasks')"
            :active-class="navLinkClass('/tasks')"
          >
            tasks
          </EngineLink>
          <EngineLink
            url="/team"
            :display-contents="false"
            :idle-class="navLinkClass('/team')"
            :active-class="navLinkClass('/team')"
          >
            team
          </EngineLink>
          <EngineLink
            url="/office"
            :display-contents="false"
            :idle-class="navLinkClass('/office')"
            :active-class="navLinkClass('/office')"
          >
            office
          </EngineLink>
          <EngineLink
            url="/events"
            :display-contents="false"
            :idle-class="navLinkClass('/events')"
            :active-class="navLinkClass('/events')"
          >
            events
          </EngineLink>
          <EngineLink
            url="/vms"
            :display-contents="false"
            :idle-class="navLinkClass('/vms')"
            :active-class="navLinkClass('/vms')"
          >
            vms
          </EngineLink>
          <EngineLink
            url="/claude"
            :display-contents="false"
            :idle-class="navLinkClass('/claude')"
            :active-class="navLinkClass('/claude')"
          >
            claude
          </EngineLink>
          <EngineLink
            url="/rc"
            :display-contents="false"
            :idle-class="navLinkClass('/rc')"
            :active-class="navLinkClass('/rc')"
          >
            rc
          </EngineLink>
        </nav>
      </div>

      <iRow
        width="hug"
        gap="small"
        vertical-align="middle"
        wrap="nowrap"
      >
        <CallBeacon />
        <PresenceAvatars
          namespace="q3po-presence-{id}"
          follow-namespace="q3po-follow-{id}"
          :ready="rootBooted"
        />
        <ActivityRecorder
          app-name="Q3po"
          event-name="RecordMyActivityBucket"
          flags-constant="teamActivityFlags"
          gate-field="myTeamMember"
          :ready="rootBooted"
        />
        <QuickFixMenu />
        <div ref="menuRoot" class="app-header-menu-root">
          <button
            type="button"
            class="app-header-menu-trigger"
            data-test="app-user-menu-trigger"
            aria-haspopup="true"
            :aria-expanded="menuOpen"
            @click="toggleMenu"
            @mouseenter="onTriggerEnter"
            @mouseleave="onTriggerLeave"
            @focus="onTriggerEnter"
            @blur="onTriggerLeave"
          >
            <img
              :src="triggerSrc"
              alt=""
              class="app-header-menu-gif"
              aria-hidden="true"
            >
            <span class="visually-hidden">Open user menu</span>
          </button>
          <ul v-if="menuOpen" class="app-header-menu" data-test="app-user-menu">
            <li @click="setMenuOpen(false)">
              <EngineLink
                url="/account"
                :display-contents="false"
                idle-class="app-header-menu-item"
                active-class="app-header-menu-item"
              >
                user settings
              </EngineLink>
            </li>
            <li @click="setMenuOpen(false)">
              <EngineLink
                url="/settings"
                :display-contents="false"
                idle-class="app-header-menu-item"
                active-class="app-header-menu-item"
              >
                org settings
              </EngineLink>
            </li>
            <li>
              <a href="/logout" class="app-header-menu-item" @click="onForceQuit">
                force quit
              </a>
            </li>
          </ul>
        </div>
      </iRow>
    </div>

    <div v-if="$slots['sub-header'] || $slots['sub-header-end']" class="sub-header">
      <slot name="sub-header" />
      <template v-if="$slots['sub-header-end']">
        <iRow width="fillv2" />
        <slot name="sub-header-end" />
      </template>
    </div>
    <FeedToasts
      v-if="rootBooted"
      app-name="Chat"
      field-name="unreadDirectMessages"
      since-field="unreadAt"
      title-template="New message from {peer.name}"
      action-url="/office"
      action-label="Open"
      label="Direct messages"
    />
  </header>
</template>

<script>
import { mapActions } from "pinia";
import CallBeacon from "@bloglovin/inspire-engine/Components/CallBeacon";
import EngineLink from "@bloglovin/inspire-engine/Components/EngineLink";
import PresenceAvatars from "@bloglovin/inspire-engine/Components/PresenceAvatars";
import ActivityRecorder from "@bloglovin/inspire-engine/Components/ActivityRecorder";
import FeedToasts from "@bloglovin/inspire-engine/Components/FeedToasts";
import { registerViewMirror, viewMirrorChanged } from "@bloglovin/inspire-engine/Helpers/viewMirror";
import QuickFixMenu from "@/components/QuickFixMenu";
import { useGeneralStore } from "@/stores/general-store";
import r2d2Gif from "@/images/r2-d2.gif";

const USER_MENU_MIRROR_KEY = "header:userMenu";

export default {
  name: "AppHeader",
  components: { ActivityRecorder, CallBeacon, EngineLink, FeedToasts, PresenceAvatars, QuickFixMenu },
  props: {
    currentPath: {
      type: String,
      required: false,
      default: "",
    },
    rootBooted: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      menuOpen: false,
      r2d2Gif,
      frozenSrc: "",
      hovering: false,
    };
  },
  computed: {
    triggerSrc() {
      if (this.hovering) {
        return this.r2d2Gif;
      }
      return this.frozenSrc || this.r2d2Gif;
    },
  },
  mounted() {
    document.addEventListener("click", this.onDocumentClick);
    document.addEventListener("keydown", this.onDocumentKeyDown);
    this.stopMenuMirror = registerViewMirror(USER_MENU_MIRROR_KEY, {
      read: () => this.menuOpen,
      apply: value => {
        this.menuOpen = value === true;
      },
    });
    this.captureFirstFrame();
  },
  beforeUnmount() {
    this.stopMenuMirror();
    document.removeEventListener("click", this.onDocumentClick);
    document.removeEventListener("keydown", this.onDocumentKeyDown);
  },
  methods: {
    ...mapActions(useGeneralStore, ["logout"]),
    isActiveNav(href) {
      const cp = this.currentPath || "";
      if (!cp || !href) {
        return false;
      }
      if (cp === href) {
        return true;
      }
      return cp.startsWith(`${href}/`);
    },
    navLinkClass(href) {
      return this.isActiveNav(href)
        ? "app-header-nav-link app-header-nav-link-active"
        : "app-header-nav-link";
    },
    setMenuOpen(open) {
      if (this.menuOpen === open) {
        return;
      }
      this.menuOpen = open;
      viewMirrorChanged(USER_MENU_MIRROR_KEY);
    },
    toggleMenu() {
      this.setMenuOpen(!this.menuOpen);
    },
    captureFirstFrame() {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = img.naturalWidth || img.width;
          canvas.height = img.naturalHeight || img.height;
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            return;
          }
          ctx.drawImage(img, 0, 0);
          this.frozenSrc = canvas.toDataURL("image/png");
        } catch (e) {
          this.frozenSrc = "";
        }
      };
      img.src = this.r2d2Gif;
    },
    onTriggerEnter() {
      this.hovering = false;
      this.$nextTick(() => {
        this.hovering = true;
      });
    },
    onTriggerLeave() {
      this.hovering = false;
    },
    onForceQuit() {
      this.setMenuOpen(false);
      this.logout();
    },
    onDocumentClick(event) {
      if (!this.menuOpen) {
        return;
      }
      const root = this.$refs.menuRoot;
      if (root && root.contains(event.target)) {
        return;
      }
      this.setMenuOpen(false);
    },
    onDocumentKeyDown(event) {
      if (event.key === "Escape" && this.menuOpen) {
        this.setMenuOpen(false);
      }
    },
  },
};
</script>

<style scoped>
.app-header {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
    padding: 24px 0 0 0;
  gap: 8px;
}

.main-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.sub-header {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 14px;
  width: 100%;
  padding: 4px 0 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.app-header-left {
  display: flex;
  align-items: baseline;
  gap: 14px;
  min-width: 0;
}

:deep(.app-header-brand-link) {
  text-decoration: none;
  color: inherit;
  outline: none;
}

:deep(.app-header-brand-link:focus-visible) .app-header-brand {
  text-decoration: underline;
}

.app-header-brand {
  margin: 0;
  font-family: 'Starjedi', 'TradeGothic', sans-serif;
  font-weight: bold;
  font-size: 32px;
  line-height: 1;
  color: yellow;
  white-space: nowrap;
}

.app-header-nav {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 18px;
  margin-left: 4px;
}

:deep(.app-header-nav-link) {
  font-family: 'Starjedi', 'TradeGothic', sans-serif;
  font-size: 20px;
  letter-spacing: 0.04em;
  text-transform: lowercase;
  color: yellow;
  text-decoration: none;
  white-space: nowrap;
  outline: none;
}

:deep(.app-header-nav-link:hover),
:deep(.app-header-nav-link:focus-visible) {
  text-decoration: underline;
}

:deep(.app-header-nav-link-active) {
  text-decoration: underline;
  text-underline-offset: 4px;
  text-decoration-thickness: 2px;
}

.app-header-menu-root {
  position: relative;
}

.app-header-menu-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
    border: 0;
  background: transparent;
  color: yellow;
  cursor: pointer;
  padding: 0;
  overflow: hidden;
}

.app-header-menu-gif {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.app-header-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 10;
  min-width: 160px;
  background: black;
  border: 1px solid yellow;
  border-radius: 4px;
  list-style: none;
  margin: 0;
  padding: 4px 0;
}

.app-header-menu-item {
  display: block;
  width: 100%;
  text-align: left;
  background: transparent;
  border: 0;
  padding: 8px 12px;
  color: yellow;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
}

.app-header-menu-item:hover {
  background: rgba(255, 255, 0, 0.15);
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
